import React, { useMemo } from 'react';
import VariablesTable from '../../VariablesPanel/VariablesTable';
import {
  campaignDefinedVariables,
  generateVariableId,
  nextVariableColor,
  variableNameKey,
} from '../../../utils/variableScope';
import useScopedVariableData from '../../VariablesPanel/VariablesTable/hooks/useScopedVariableData';
import { getSharedRowsRef } from '../../VariablesPanel/VariablesTable/hooks/sharedVariableRowsRef';
import { buildVariableOfType } from '../../../utils/variableTypeChange';
import LinkedVariableRowPicker from './LinkedVariableRowPicker';
import '../styles/variables-block.css';

/**
 * LinkedVariableRowBlock — one variables-table row, bound by `props.variableId`.
 * Edits the same stored row as the main table (and every other linked row).
 */
export const LinkedVariableRowBlock = ({
  block,
  onUpdate,
  docVariables = [],
  onDocVariablesChange = null,
  onRenameVariable = null,
  variablesHistoryEpoch = 0,
  variableCampaign = null,
  allSprints = [],
  onCampaignVariablesChange = null,
  missionVariables = null,
  onMissionVariablesChange = null,
  onPatchInheritedVariable = null,
  variablesColumnWidths = null,
  onVariablesColumnWidthsChange = null,
}) => {
  const bindId = block.props?.variableId || null;
  const cachedName = block.props?.variableName || '';
  const campaignWriter = !!(onCampaignVariablesChange && variableCampaign);
  const campaignRows = campaignWriter ? variableCampaign?.variables || [] : [];
  const definedCampaignRows = useMemo(
    () => campaignDefinedVariables(campaignRows),
    [campaignRows]
  );
  const docRows = onDocVariablesChange ? docVariables || [] : [];

  const { inheritedRows, pickerVariables } = useScopedVariableData({
    hasDocScope: !!onDocVariablesChange,
    hasCampaignScope: campaignWriter,
    docVariables,
    docRows,
    campaignRows,
    campaign: variableCampaign,
    allSprints,
    missionVariables,
  });

  // Same names the variables table shows. Raw campaign.variables still holds
  // doc value copies, and listing those made them look like campaign variables.

  const bound = useMemo(() => {
    if (!bindId) return null;
    return (
      docRows.find((v) => v.id === bindId) ||
      definedCampaignRows.find((v) => v.id === bindId) ||
      inheritedRows.find((v) => v.id === bindId) ||
      null
    );
  }, [bindId, definedCampaignRows, inheritedRows, docRows]);

  const bind = (variable) => {
    if (!variable?.id) return;
    onUpdate?.(block.id, {
      props: {
        ...(block.props || {}),
        variableId: variable.id,
        variableName: variable.name || '',
      },
    });
  };

  const handleCreate = (name, type) => {
    const trimmed = String(name || '').trim().replace(/\s+/g, '_');
    const writer = onDocVariablesChange;
    if (!trimmed || !writer) return;
    const existing = pickerVariables.find(
      (v) => variableNameKey(v.name) === variableNameKey(trimmed)
    );
    if (existing) {
      bind(existing);
      return;
    }
    const seed = docRows;
    const live = getSharedRowsRef(writer, seed)?.current || seed;
    const next = buildVariableOfType(
      {
        id: generateVariableId(),
        name: trimmed,
        description: '',
        color: nextVariableColor([...live, ...inheritedRows]),
        locked: false,
      },
      type
    );
    const list = [...live, next];
    const shared = getSharedRowsRef(writer, list);
    if (shared) shared.current = list;
    writer(list);
    bind(next);
  };

  if (!onDocVariablesChange && !campaignWriter) {
    return (
      <div className="variables-block linked-variable-row-block variables-block--readonly" contentEditable={false}>
        <div className="variables-block__empty">Variables unavailable</div>
      </div>
    );
  }

  if (!bound) {
    return (
      <div className="variables-block linked-variable-row-block" contentEditable={false}>
        <LinkedVariableRowPicker
          variables={pickerVariables}
          missingName={bindId ? cachedName : ''}
          onPick={bind}
          onCreate={handleCreate}
        />
      </div>
    );
  }

  return (
    <div className="variables-block linked-variable-row-block" contentEditable={false}>
      <VariablesTable
        singleVariableId={bindId}
        docVariables={docVariables}
        onDocVariablesChange={onDocVariablesChange}
        onRenameVariable={onRenameVariable}
        historyEpoch={variablesHistoryEpoch}
        campaign={variableCampaign}
        onCampaignVariablesChange={
          campaignWriter ? onCampaignVariablesChange : null
        }
        missionVariables={missionVariables}
        onMissionVariablesChange={onMissionVariablesChange}
        onPatchInheritedVariable={onPatchInheritedVariable}
        allSprints={allSprints}
        columnWidths={variablesColumnWidths}
        onColumnWidthsChange={onVariablesColumnWidthsChange}
      />
    </div>
  );
};
